import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, ClockLine} from '@dropbox/dig-icons/assets';
import {DocumentMini} from '@dropbox/dig-illustrations';
import {$generateHtmlFromNodes} from '@lexical/html';
import {useMutation, useQuery} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import {DocumentService, DocumentVersion} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {Facepile} from 'components/DSYS/Facepile';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Title} from 'components/DSYS/Title';
import {YjsToEditorStateConverter} from 'components/DSYS/YjsToEditorStateConverter';
import {toLocaleStringWithTime} from 'components/shared/TimeAgo';
import {diffHtml} from 'helpers/diffHtml';
import {isProd} from 'helpers/environment';
import {reportAndLogError} from 'helpers/logging';
import {useYjsDocument} from 'hooks/useYjsDocument';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useCallback, useEffect, useState} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

import styles from './Planning.module.css';

export const useDocumentVersions = (
  documentId: string,
  {enabled = true}: {enabled?: boolean} = {}
) => {
  const {data: versions, isLoading} = useQuery({
    queryKey: ['document', 'versions', documentId],
    queryFn: () =>
      getService(DocumentService).listVersionsApiV1DocumentDocumentIdVersionsGet(documentId),
    enabled: Boolean(documentId) && enabled,
  });

  return {versions, isLoading};
};

export const useDocumentVersionMutations = (documentId: string) => {
  const setSnackbar = useSetAtom(snackbarAtom);

  const {mutateAsync: createVersion} = useMutation({
    mutationFn: ({label}: {label?: string}) =>
      getService(DocumentService).createVersionApiV1DocumentDocumentIdVersionsPost(documentId, {
        label,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['document', 'versions', documentId]});
      setSnackbar({text: t('version_created')});
    },
    onError: (error) => {
      reportAndLogError(error);
      setSnackbar({text: t('failed_to_create_version')});
    },
  });

  const {mutateAsync: rollbackVersion} = useMutation({
    mutationFn: ({versionId}: {versionId: string}) =>
      getService(
        DocumentService
      ).rollbackVersionApiV1DocumentDocumentIdVersionsVersionIdRollbackPost(documentId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['document', 'versions', documentId]});
      setSnackbar({text: t('version_rolled_back')});
      // Reload the page to show the rolled back version
      window.location.reload();
    },
    onError: (error) => {
      reportAndLogError(error);
      setSnackbar({text: t('failed_to_rollback_version')});
    },
  });

  return {createVersion, rollbackVersion};
};

export const PlanningVersionsDrawer = ({
  documentId,
  onVersionClick,
}: {
  documentId: string;
  onVersionClick: (version: DocumentVersion) => void;
}) => {
  const {versions, isLoading} = useDocumentVersions(documentId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Spinner size="medium" />
      </Box>
    );
  }

  if (!versions || versions.length === 0) {
    return <PlanningVersionsDrawerEmptyState />;
  }

  // Sort versions by created date, most recent first
  const sortedVersions = [...versions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Box display="flex" flexDirection="column" style={{gap: '12px'}}>
      {sortedVersions.map((version) => (
        <Box
          key={version.id}
          cursor="pointer"
          borderRadius="Medium"
          onClick={() => onVersionClick(version)}
          paddingX="16"
          paddingY="16"
          {...withShade({})}
        >
          <Box display="flex" flexDirection="column" style={{gap: '8px'}}>
            {version.label ? (
              <>
                <Text isBold>{version.label}</Text>
                <Text color="subtle" size="small">
                  {toLocaleStringWithTime(version.created_at + 'Z')}
                </Text>
              </>
            ) : (
              <Text isBold>{toLocaleStringWithTime(version.created_at + 'Z')}</Text>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const useVersionPreviewChildSheet = (
  documentId: string,
  {
    enabled = true,
    canRollbackVersion = false,
  }: {enabled?: boolean; canRollbackVersion?: boolean} = {}
) => {
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);
  const [versionIds, setVersionIds] = useState<{selected: string | null; previous: string | null}>({
    selected: null,
    previous: null,
  });
  const [html, setHtml] = useState<{current: string | null; previous: string | null}>({
    current: null,
    previous: null,
  });
  const [diffResult, setDiffResult] = useState<{beforeHtml: string; afterHtml: string} | null>(
    null
  );
  const [hideUnchanged, setHideUnchanged] = useState(false);

  const {rollbackVersion} = useDocumentVersionMutations(documentId);
  const {versions} = useDocumentVersions(documentId, {enabled});
  const setSnackbar = useSetAtom(snackbarAtom);

  // Load the selected version content (AFTER/newer)
  const {yjsContent: selectedVersionContent, error: selectedError} = useYjsDocument(
    versionIds.selected,
    !!versionIds.selected
  );

  // Load the previous version content (BEFORE/older)
  const {yjsContent: previousVersionContent, error: previousError} = useYjsDocument(
    versionIds.previous,
    !!versionIds.previous
  );

  // Clear everything
  const clearAll = useCallback(() => {
    setSelectedVersion(null);
    setVersionIds({selected: null, previous: null});
    setHtml({current: null, previous: null});
    setDiffResult(null);
    setHideUnchanged(false);
  }, []);

  // Handle loading errors
  useEffect(() => {
    if (selectedError || previousError) {
      reportAndLogError(selectedError || previousError);
      setSnackbar({text: t('failed_to_load_version')});
      clearAll();
    }
  }, [selectedError, previousError, setSnackbar, clearAll]);

  // Clear HTML when version IDs change to prevent stale data
  useEffect(() => {
    setHtml({current: null, previous: null});
    setDiffResult(null);
  }, [versionIds]);

  // Compute diff when both HTML strings are ready
  useEffect(() => {
    if (html.previous && html.current) {
      const result = diffHtml(html.previous, html.current, {hideUnchanged});
      setDiffResult(result);
    }
  }, [html, hideUnchanged]);

  // Check if there are any changes in the diff
  const hasChanges =
    diffResult && (diffResult.afterHtml.includes('<ins') || diffResult.afterHtml.includes('<del'));

  const handleVersionClick = useCallback(
    (version: DocumentVersion) => {
      setSelectedVersion(version);
      setHideUnchanged(false);

      // Find the version before this one (chronologically older)
      if (!versions) return;

      const sortedVersions = [...versions].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      const currentIndex = sortedVersions.findIndex((v) => v.id === version.id);

      // Set the previous version (the one chronologically before the selected one)
      if (currentIndex >= 0 && currentIndex < sortedVersions.length - 1) {
        setVersionIds({selected: version.id, previous: sortedVersions[currentIndex + 1].id});
      } else {
        // This is the oldest version, no previous version to compare with
        setVersionIds({selected: version.id, previous: null});
      }
    },
    [versions]
  );

  const handleRollback = useCallback(async () => {
    if (!selectedVersion || !canRollbackVersion) return;
    await rollbackVersion({versionId: selectedVersion.id});
    clearAll();
  }, [selectedVersion, canRollbackVersion, rollbackVersion, clearAll]);

  const childSheet = selectedVersion
    ? {
        icon: ClockLine,
        header: (
          <Stack gap="16">
            <Title size={18}>
              {selectedVersion.label || 'Version'}{' '}
              <Text color="subtle" size="small">
                {toLocaleStringWithTime(selectedVersion.created_at + 'Z')}
              </Text>
            </Title>
            {canRollbackVersion && (
              <Button size="small" variant="outline" onClick={handleRollback}>
                {t('rollback')}
              </Button>
            )}
          </Stack>
        ),
        body: (
          <Box display="flex" flexDirection="column" style={{gap: '16px'}}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{gap: '12px'}}
            >
              {selectedVersion.editors && selectedVersion.editors.length > 0 && (
                <Facepile ldaps={selectedVersion.editors.map((editor) => editor.ldap)} />
              )}
              {false && versionIds.previous && (
                <Box display="flex" style={{gap: '8px', marginLeft: 'auto'}}>
                  <Button
                    size="small"
                    variant={!hideUnchanged ? 'primary' : 'outline'}
                    onClick={() => setHideUnchanged(!hideUnchanged)}
                  >
                    {hideUnchanged ? t('show_unchanged') : t('hide_unchanged')}
                  </Button>
                </Box>
              )}
            </Box>

            {!selectedVersionContent || (versionIds.previous && !diffResult) ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{height: '400px'}}
              >
                <Spinner size="medium" />
              </Box>
            ) : versionIds.previous && diffResult && !hasChanges ? (
              <EmptyState
                hideBorder
                title={t('version_no_changes')}
                body={t('version_no_changes_body')}
                image={<DocumentMini altText="document" width={64} height={64} />}
              />
            ) : versionIds.previous && diffResult ? (
              <Box
                width="100%"
                marginLeft="auto"
                marginRight="auto"
                className={styles.disableMarks}
                style={{maxWidth: 765}}
              >
                <RichTextArea
                  key={`version-diff-${versionIds.selected}`}
                  value={diffResult.afterHtml}
                  editable={false}
                  profile="paper"
                  enhancedTableCells
                  table
                  media
                />
              </Box>
            ) : (
              <Box width="100%" marginLeft="auto" marginRight="auto" style={{maxWidth: 765}}>
                <RichTextArea
                  key={`version-preview-${versionIds.selected}`}
                  yjsValue={selectedVersionContent}
                  editable={false}
                  enhancedTableCells
                  table
                  media
                  profile="paper"
                />
              </Box>
            )}

            {/* Hidden converters to convert Yjs to HTML */}
            {/* Always: selected version = AFTER (new), previous version = BEFORE (old) */}
            {selectedVersionContent && versionIds.selected && (
              <YjsToEditorStateConverter
                key={`selected-${versionIds.selected}`}
                conversionKey={`selected-${versionIds.selected}`}
                yjsValue={selectedVersionContent}
                onDocumentReady={(editor) => {
                  editor.getEditorState().read(() => {
                    setHtml((prev) => ({...prev, current: $generateHtmlFromNodes(editor)}));
                  });
                }}
              />
            )}
            {previousVersionContent && versionIds.previous && (
              <YjsToEditorStateConverter
                key={`previous-${versionIds.previous}`}
                conversionKey={`previous-${versionIds.previous}`}
                yjsValue={previousVersionContent}
                onDocumentReady={(editor) => {
                  editor.getEditorState().read(() => {
                    setHtml((prev) => ({...prev, previous: $generateHtmlFromNodes(editor)}));
                  });
                }}
              />
            )}
          </Box>
        ),
        onClose: clearAll,
      }
    : undefined;

  return {
    selectedVersion,
    childSheet,
    handleVersionClick,
    clearVersion: clearAll,
  };
};

export const PlanningVersionsDrawerHeader = ({documentId}: {documentId: string}) => {
  const setSnackbar = useSetAtom(snackbarAtom);
  const {versions, isLoading} = useDocumentVersions(documentId);
  const {createVersion} = useDocumentVersionMutations(documentId);

  const versionCount = versions?.length ?? 0;

  const handleCreateVersion = useCallback(async () => {
    const label = prompt('Enter a label for this version (optional):');
    if (label === null) return; // User cancelled

    try {
      await createVersion({label: label || undefined});
    } catch (error) {
      reportAndLogError(error);
      setSnackbar({text: t('failed_to_create_version')});
    }
  }, [createVersion, setSnackbar]);

  return (
    <Stack gap="12">
      {isLoading ? (
        <Box paddingTop="2" style={{width: 120, height: 24}}>
          <Skeleton.Text size="large" width={120} />
        </Box>
      ) : (
        <Title size={18}>{t('versions', {count: versionCount})}</Title>
      )}
      {!isProd && (
        <Button
          size="small"
          variant="opacity"
          onClick={handleCreateVersion}
          withIconStart={<UIIcon src={AddLine} />}
        >
          {t('create_version')}
        </Button>
      )}
    </Stack>
  );
};

const PlanningVersionsDrawerEmptyState = () => (
  <EmptyState
    hideBorder
    title={t('no_versions')}
    body={t('versions_will_appear_here')}
    image={<DocumentMini altText="document" width={64} height={64} />}
  />
);
